import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { getExam, getExamResults, bulkSaveResults } from "../../api/examApi";
import axios from "axios";

const ExamResultsEntryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const examData = await getExam(id);
      if (examData?.success) {
        setExam(examData.exam);
        // Load students enrolled in the exam's grade for the session
        try {
          const enrollResp = await axios.get("/api/enrollments", {
            params: { grade_id: examData.exam.grade_id, session_id: examData.exam.session_id },
          });
          const enrollments = Array.isArray(enrollResp.data) ? enrollResp.data : enrollResp.data?.items || [];
          const studs = enrollments.map((e) => e.student).filter(Boolean);
          setStudents(studs);

          // Load existing results
          const resultsData = await getExamResults({ examId: id });
          const existing = resultsData?.success ? resultsData.items || [] : [];
          const existingMap = new Map(existing.map((r) => [r.student_id, r]));

          setRows(
            studs.map((s) => {
              const r = existingMap.get(s.id);
              return {
                student_id: s.id,
                student_name: s.full_name,
                marks_obtained: r?.marks_obtained ?? null,
                grade_letter: r?.grade_letter ?? "",
                remarks: r?.remarks ?? "",
                is_absent: r?.is_absent ?? false,
              };
            })
          );
        } catch (e) {
          // If endpoint doesn't return expected shape, fall back to no rows
        }
      }
    };
    load();
  }, [id]);

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = rows.map((r) => ({
        student_id: r.student_id,
        marks_obtained: r.is_absent ? null : r.marks_obtained,
        grade_letter: r.grade_letter,
        remarks: r.remarks,
        is_absent: r.is_absent,
      }));
      await bulkSaveResults(id, payload);
      toast.success("Results saved");
    } catch (e) {
      toast.error("Failed to save results");
    } finally {
      setSaving(false);
    }
  };

  if (!exam) return <Card>Loading...</Card>;

  return (
    <Card>
      <div className="flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="m-0">{exam.name} — Enter Results</h2>
          <div className="text-sm opacity-70 mt-1">
            Total marks: {exam.total_marks} · Passing: {exam.passing_marks}
          </div>
        </div>
        <div className="flex gap-2">
          <Button label="Back" className="p-button-text" onClick={() => navigate("/index/exams")} />
          <Button label="Save All" icon="pi pi-save" onClick={handleSave} loading={saving} />
        </div>
      </div>
      <DataTable value={rows} stripedRows emptyMessage="No students found.">
        <Column field="student_name" header="Student" />
        <Column
          header="Absent"
          body={(row, opts) => (
            <Checkbox
              checked={row.is_absent}
              onChange={(e) => updateRow(opts.rowIndex, { is_absent: e.checked })}
            />
          )}
          style={{ width: "80px" }}
        />
        <Column
          header={`Marks (max ${exam.total_marks})`}
          body={(row, opts) => (
            <InputNumber
              value={row.marks_obtained}
              onValueChange={(e) => updateRow(opts.rowIndex, { marks_obtained: e.value })}
              disabled={row.is_absent}
              max={exam.total_marks}
              min={0}
            />
          )}
        />
        <Column
          header="Grade"
          body={(row, opts) => (
            <InputText
              value={row.grade_letter}
              onChange={(e) => updateRow(opts.rowIndex, { grade_letter: e.target.value })}
              style={{ width: "60px" }}
            />
          )}
        />
        <Column
          header="Remarks"
          body={(row, opts) => (
            <InputText
              value={row.remarks}
              onChange={(e) => updateRow(opts.rowIndex, { remarks: e.target.value })}
              style={{ width: "100%" }}
            />
          )}
        />
      </DataTable>
    </Card>
  );
};

export default ExamResultsEntryPage;
